/* eslint-disable jsx-a11y/anchor-is-valid */
import {FC, useState, useEffect, useRef} from 'react'
import {ID} from '../../../../../../_metronic/helpers'
import clsx from 'clsx'
import {useMutation, useQueryClient} from 'react-query'
import {deliveryTask, deliveryTaskPartial, playTimeTracking, stopTimeTracking} from '../../../core/_requests'
import {Toast} from '../../../../../../_metronic/partials/widgets'
import {useIntl} from 'react-intl'
import Swal from 'sweetalert2'
import {useQueryResponse} from '../../../core/QueryResponseProvider'
import {useListView} from '../../../core/ListViewProvider'
import {UserModel, useAuth} from '../../../../auth'
import {DeliveryOptionsModal} from '../../../../../../_metronic/partials/modals/task-delivery/DeliveryOptionsModal'
import {invalidateAfterTaskChange} from '../../../../../../_metronic/helpers/taskQueryInvalidation'

type Props = {
  task_id: ID
  time: number
  play: string
  rowIndex: number
  columnId: number
  updateCellData: (rowIndex: number, columnId: number, value: string) => void
  status: string
  name: string
  sequenceResponsibles: UserModel[]
  users?: Array<{user_id?: number; id?: number; task_concluded?: number; pivot?: {concluded?: number}; [key: string]: unknown}>
}

const MyTaskTimeTrackingCell: FC<Props> = ({
  task_id,
  time,
  play,
  rowIndex,
  columnId,
  updateCellData,
  status,
  name,
  sequenceResponsibles,
  users = [],
}) => {
  const {currentUser, setCurrentUser} = useAuth()
  const [time2, setTime] = useState(time!)
  const hasSequence = !!(sequenceResponsibles && sequenceResponsibles.length > 0)
  const myPartConcluded =
    !hasSequence &&
    status?.toLowerCase() !== 'entregue' &&
    !!currentUser?.id &&
    (users as Array<{user_id?: number; id?: number; task_concluded?: number; pivot?: {concluded?: number}}>).some(
      (u) =>
        Number(u.user_id ?? u.id) === Number(currentUser.id) &&
        Number(u.task_concluded ?? u.pivot?.concluded ?? 0) === 1
    )
  const showPlayAndDeliver = status?.toLowerCase() !== 'entregue' && !myPartConcluded
  const [play2, setPlay] = useState(play)
  const [showDeliveryModal, setShowDeliveryModal] = useState(false)
  const countRef = useRef()
  const [stopwatch, setStopwatch] = useState(0)
  const intl = useIntl()
  const queryClient = useQueryClient()
  const {refetch, response} = useQueryResponse()
  const {setItemIdForUpdate} = useListView()
  const TemaAtual = window.localStorage.getItem('kt_theme_mode_value') || ''

  // Sincroniza com o servidor a cada refetch da lista (não só quando o valor de "play"
  // muda): dar play numa tarefa de outro usuário não altera o "play" desta linha no
  // servidor, então depender só do valor deixaria o otimismo do clique preso em 'run'
  // para sempre, mesmo sem refletir o timer de ninguém de verdade.
  useEffect(() => {
    setPlay(play)
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [play, response])

  useEffect(() => {
    if (play2 === 'run') {
      const newTime = time > time2 ? time : time2 + 1
      const intervalId = window.setInterval(() => setTime(newTime), 1000)
      return () => window.clearInterval(intervalId)
    } else {
      setStopwatch(time2)
    }

    return () => clearInterval(countRef.current)
  }, [play2, time2, time])

  const hours = Math.floor(time2 / 3600)
  const minutes = Math.floor((time2 % 3600) / 60)
  const seconds = time2 % 60

  const startAndStop = () => {
    //Start, envia o tempo para Insert/Update na API
    if (play2 === 'pause') {
      playItem.mutate()
    }
    // //Pausou, envia o tempo para insert/update na API
    else {
      stopItem.mutate()
    }
  }

  const playItem = useMutation(() => playTimeTracking(task_id), {
    onSuccess: (response) => {
      Toast.fire({
        icon: 'success',
        title: intl.formatMessage(
          {id: 'ALERT.LABEL.SUCCESS_TIMETRACKING_START'},
          {item: intl.formatMessage({id: 'TABLE.GENERAL.LABEL.TIMETRACKING'})}
        ),
        customClass: {
          popup: 'bg-light-success',
          title: 'text-success',
        },
      })

      invalidateAfterTaskChange(queryClient)
      setCurrentUser((user) => {
        return {...user, has_active_time_tracking: true} as UserModel
      })
      setPlay('run')
    },
    onError: (error: any) => {
      Toast.fire({
        icon: 'error',
        title: error.response?.data?.message || 'Ocorreu um erro inesperado',
        customClass: {
          popup: 'bg-light-danger',
          title: TemaAtual === 'dark' ? 'text-dark' : 'text-danger',
        },
      })
    },
  })

  const stopItem = useMutation(() => stopTimeTracking(task_id), {
    // 💡 response of the mutation is passed to onSuccess
    onSuccess: (response) => {
      Toast.fire({
        icon: 'success',
        title: intl.formatMessage(
          {id: 'ALERT.LABEL.SUCCESS_TIMETRACKING_STOP'},
          {item: intl.formatMessage({id: 'TABLE.GENERAL.LABEL.TIMETRACKING'})}
        ),
        customClass: {
          popup: 'bg-light-success',
          title: 'text-success',
        },
      })

      invalidateAfterTaskChange(queryClient)
      setCurrentUser((user) => {
        return {...user, has_active_time_tracking: false} as UserModel
      })
      setPlay('pause')
    },
  })

  const entregatask = async () => {
    if (sequenceResponsibles.length > 0) {
      const CurrentTheme = window.localStorage.getItem('kt_theme_mode_value') || ''
      const tema = CurrentTheme === 'dark' ? '#353b48' : '#fff'
      const swalWithBootstrapButtons = Swal.mixin({
        customClass: { confirmButton: 'btn btn-success', cancelButton: 'btn btn-danger' },
        buttonsStyling: false,
      })
      swalWithBootstrapButtons
        .fire({
          title: intl.formatMessage({id: 'ALERT.TITLE.TASK.DELIVERY'}),
          icon: 'warning',
          text: 'Esta tarefa possui outros membros na sequência. Ao entregar, ela será transferida para o próximo responsável. Deseja entregar sua parte?',
          background: tema,
          showCancelButton: true,
          confirmButtonText: 'Sim, entregar minha parte',
          cancelButtonText: 'Não',
          reverseButtons: true,
        })
        .then((result) => {
          if (result.isConfirmed) deliveryItem.mutate()
          else if (result.dismiss === Swal.DismissReason.cancel) {
            Toast.fire({
              icon: 'warning',
              title: intl.formatMessage(
                {id: 'ALERT.LABEL.ERROR_TASK_CLOSE_ITEM'},
                {item: intl.formatMessage({id: 'TABLE.GENERAL.LABEL.TASK'})}
              ),
              customClass: { popup: 'bg-light-danger', title: 'text-danger' },
            })
          }
        })
      return
    }
    if (users && users.length > 1) {
      setShowDeliveryModal(true)
      return
    }
    const CurrentTheme = window.localStorage.getItem('kt_theme_mode_value') || ''
    const tema = CurrentTheme === 'dark' ? '#353b48' : '#fff'
    const swalWithBootstrapButtons = Swal.mixin({
      customClass: { confirmButton: 'btn btn-success', cancelButton: 'btn btn-danger' },
      buttonsStyling: false,
    })
    swalWithBootstrapButtons
      .fire({
        title: intl.formatMessage({id: 'ALERT.TITLE.TASK.DELIVERY'}),
        icon: 'warning',
        background: tema,
        showCancelButton: true,
        confirmButtonText: 'Sim',
        cancelButtonText: 'Não',
        reverseButtons: true,
      })
      .then((result) => {
        if (result.isConfirmed) deliveryItem.mutate()
        else if (result.dismiss === Swal.DismissReason.cancel) {
          Toast.fire({
            icon: 'warning',
            title: intl.formatMessage(
              {id: 'ALERT.LABEL.ERROR_TASK_CLOSE_ITEM'},
              {item: intl.formatMessage({id: 'TABLE.GENERAL.LABEL.TASK'})}
            ),
            customClass: { popup: 'bg-light-danger', title: 'text-danger' },
          })
        }
      })
  }

  const deliveryItem = useMutation(() => deliveryTask(task_id), {
    onSuccess: (response) => {
      Toast.fire({
        icon: response.status === 'success' ? 'success' : 'error',
        title: response.message,
        customClass: {
          popup: response.status === 'success' ? 'bg-light-success' : 'bg-light-error',
          title: TemaAtual === 'dark' ? 'text-dark' : 'text-success',
        },
      })

      if (play2 === 'run')
        setCurrentUser((user) => ({...user, has_active_time_tracking: false} as UserModel))

      cancel(true)
      invalidateAfterTaskChange(queryClient)
    },
    onError: (error: any) => {
      Toast.fire({
        icon: 'error',
        title: error.response?.data?.message || 'Ocorreu um erro inesperado',
        customClass: {
          popup: 'bg-light-danger',
          title: TemaAtual === 'dark' ? 'text-dark' : 'text-danger',
        },
      })
    },
  })

  const deliveryPartialItem = useMutation(() => deliveryTaskPartial(task_id), {
    onSuccess: (response) => {
      Toast.fire({
        icon: response.status === 'success' ? 'success' : 'error',
        title: response.message,
        customClass: {
          popup: response.status === 'success' ? 'bg-light-success' : 'bg-light-error',
          title: TemaAtual === 'dark' ? 'text-dark' : 'text-success',
        },
      })
      if (play2 === 'run')
        setCurrentUser((user) => ({...user, has_active_time_tracking: false} as UserModel))
      cancel(true)
      invalidateAfterTaskChange(queryClient)
    },
    onError: (error: any) => {
      Toast.fire({
        icon: 'error',
        title: error.response?.data?.message || 'Ocorreu um erro inesperado',
        customClass: {
          popup: 'bg-light-danger',
          title: TemaAtual === 'dark' ? 'text-dark' : 'text-danger',
        },
      })
    },
  })

  const cancel = (withRefresh?: boolean) => {
    if (withRefresh) {
      refetch()
    }
    setItemIdForUpdate(undefined)
  }

  return (
    <>
      {name !== '' && (
        <div className='d-flex flex-column justify-content-center'>
          {/*< TIMETRACKING />*/}
          <div className='row'>
            <div
              className='col-md-6 columnTimeTracking '
              style={{display: 'inline-flex', gap: '12px', alignItems: 'center'}}
            >
              {showPlayAndDeliver && (
                <div style={{display: 'inline-flex', gap: '4px', alignItems: 'center'}}>
                  <div
                    className='d-flex align-items-center timetracking-action-icon'
                    onClick={startAndStop}
                  >
                    {play2 === 'run' ? (
                      <i className='bi bi-pause-circle-fill cursor-pointer fs-3 text-success'></i>
                    ) : (
                      <i className='bi bi-play-circle-fill cursor-pointer fs-3 text-primary'></i>
                    )}
                  </div>

                  <div
                    className='d-flex align-items-center timetracking-action-icon'
                    onClick={entregatask}
                  >
                    <i className='bi bi-check2-circle cursor-pointer fs-3 text-primary'></i>
                  </div>
                </div>
              )}

              {play2 === 'run' ? (
                <p className={clsx('bg-transparent fs-5 mb-0 text-success')}>
                  {hours}:{minutes.toString().padStart(2, '0')}:
                  {seconds.toString().padStart(2, '0')}
                  {/*:{milliseconds.toString().padStart(2, "0")}*/}
                </p>
              ) : (
                <p className={clsx('bg-transparent fs-5 mb-0 text-primary')}>
                  {hours}:{minutes.toString().padStart(2, '0')}:
                  {seconds.toString().padStart(2, '0')}
                  {/*:{milliseconds.toString().padStart(2, "0")}*/}
                </p>
              )}
            </div>
          </div>
          {/*< TIMETRACKING />*/}
        </div>
      )}
      <DeliveryOptionsModal
        show={showDeliveryModal}
        onClose={() => setShowDeliveryModal(false)}
        onDeliverFull={() => deliveryItem.mutate()}
        onDeliverPartial={() => deliveryPartialItem.mutate()}
        taskName={name}
      />
    </>
  )
}

export {MyTaskTimeTrackingCell}
